/**
 * OziExplorer のトラック/ポリゴンファイル（`.plt`）の解析。
 *
 * 競技本部（TV/Taskview）がXDD等のタスクで「スケッチ」（地面に描く図形）を
 * この形式で配ることがある。プレーンテキストで、緯度経度の並びだけの
 * 単純な形式:
 *
 * ```
 * OziExplorer Track Point File Version 2.1
 * WGS 84
 * Altitude is in Feet
 * Reserved 3
 * 0,2,16711680,14XDD LOMNICA outline,0,0,2,8421376,-1,0
 * 67
 * 49.09751483,20.34930978,1,-777,0,,
 * 49.09755323,20.35615869,0,-777,0,,
 * ...
 * ```
 *
 * 5行目がメタ情報（3列目=色、COLORREF。4列目=図形名）、6行目が点数
 * （情報用で、実際の点数は末尾まで数えればよいので使わない）。
 * 7行目以降が点の並び: `緯度,経度,コード,標高,日付,...`。
 * **コード1は「ペンアップ＝新しい閉図形の開始」**を意味し、複数の
 * 独立した多角形（穴あき文字のような複雑な形も含む）を1ファイルで
 * 表せる。閉図形は先頭点と末尾点が一致する形で書かれている（実データで確認済み）。
 */

/** 1つの閉じたリング（先頭≒末尾）。[経度, 緯度] の並び（GeoJSONの座標順） */
export interface OziPltPolygon {
  ring: [number, number][];
}

export interface OziPlt {
  /** メタ情報の図形名（例: '14XDD LOMNICA outline'） */
  name: string;
  /** メタ情報の色。COLORREF（Windowsの0x00bbggrr）を`rgb(r, g, b)`に変換したもの */
  color: string;
  polygons: OziPltPolygon[];
}

/** 先頭行でOziExplorerのトラック/ポリゴンファイルかどうかを判定する */
export function looksLikeOziPlt(text: string): boolean {
  return /^OziExplorer\s+Track\s+Point\s+File/i.test(text.trimStart());
}

/**
 * COLORREF（Windowsの`0x00bbggrr`、リトルエンディアン格納なのでバイト順は
 * 下位からR,G,B）を`rgb(r, g, b)`のCSS表記にする。
 */
function colorRefToRgb(value: number): string {
  const r = value & 0xff;
  const g = (value >> 8) & 0xff;
  const b = (value >> 16) & 0xff;
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * 座標欄を数値化する。`Number('')`が`0`になる（＝空欄の行が緯度経度
 * `0,0`の点として紛れ込む）のを防ぐため、空欄・非数値ははっきり`null`
 * として弾く（コードレビュー指摘、2026-09-13。`,,,,,,`のような空行が
 * 実データの図形末尾に混ざっていた）。ついでに明らかに範囲外の値
 * （緯度±90度・経度±180度を超える）も同様に弾く。
 */
function parseCoordField(raw: string | undefined, min: number, max: number): number | null {
  if (raw === undefined || raw.trim() === '') return null;
  const v = Number(raw);
  return Number.isFinite(v) && v >= min && v <= max ? v : null;
}

/**
 * OziExplorerの`.plt`を解析する。
 *
 * ★ 座標欄以外（点数の宣言・標高・日付）は当てにしない。実データでも
 *   点数宣言と実際の行数が一致していたが、ズレていても最後まで読めば
 *   同じ結果になるようにしてある。
 *
 * @returns 形式自体が違う場合はnull。読める点が1つも無い場合はpolygonsが空
 */
export function parseOziPlt(text: string): OziPlt | null {
  if (!looksLikeOziPlt(text)) return null;

  const lines = text.split(/\r?\n/);
  const meta = (lines[4] ?? '').split(',');
  const colorValue = Number(meta[2]);
  const color = colorRefToRgb(Number.isFinite(colorValue) ? colorValue : 0);
  const name = (meta[3] ?? '').trim();

  const polygons: OziPltPolygon[] = [];
  let ring: [number, number][] = [];

  // 2点以下は面にならない（直線・単独点）ため、不正な図形として捨てる
  const pushRing = (): void => {
    if (ring.length >= 3) polygons.push({ ring });
    ring = [];
  };

  for (const line of lines.slice(6)) {
    if (line.trim().length === 0) continue;
    const parts = line.split(',');
    const lat = parseCoordField(parts[0], -90, 90);
    const lon = parseCoordField(parts[1], -180, 180);
    const code = Number(parts[2]);
    if (lat === null || lon === null) continue;

    if (code === 1 && ring.length > 0) pushRing();
    ring.push([lon, lat]);
  }
  pushRing();

  return { name, color, polygons };
}
